'use client'

import { ResponseCode } from '@marketplace-web/core-api/api-client-util'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  conversationReplyEvent,
  deleteConversationSuspicion,
  postReply,
  postReplyNew,
  transformConversationResponse,
  transformMessagesEntities,
  transformPostReplyNewResp,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { PersonalInformationSharingContentType } from '../../constants/personal-information-sharing'

type Props = {
  value?: string | null
  photoTempUuids?: Array<string> | null
  isPersonalDataSharingCheckSkipped?: boolean
  onSetPersonalInformationSharingContentType?: (
    contentType: PersonalInformationSharingContentType | null,
  ) => void
}

const usePostReply = () => {
  const { track } = useTracking()
  const {
    conversation,
    getConversationRequest,
    setConversation,
    setSendMessageUi,
    setConversationMessages,
    conversationMessages,
  } = useConversationContext()

  const userId = useSession().user?.id
  const isWebMessagingSvcFSEnabled = useFeatureSwitch('web_messaging_svc')
  const webPostReplyOptimizationAbTest = useAbTest('web_post_reply_optimization')

  useTrackAbTest(webPostReplyOptimizationAbTest)

  const postReplyRequestCurrent = async ({
    value,
    photoTempUuids,
    isPersonalDataSharingCheckSkipped = false,
    onSetPersonalInformationSharingContentType,
  }: Props) => {
    setSendMessageUi({ uiState: UiState.Pending })

    if (!conversation?.id || !userId) return

    const response = await postReply({
      conversationId: conversation.id,
      body: value,
      photoTempUuids,
      isPersonalDataSharingCheckSkipped,
    })

    if (response?.code === ResponseCode.EmailSharingDetected && 'errors' in response) {
      if (
        response.payload &&
        typeof response.payload === 'object' &&
        'content_type' in response.payload
      ) {
        onSetPersonalInformationSharingContentType?.(
          response.payload.content_type as PersonalInformationSharingContentType,
        )
      } else {
        onSetPersonalInformationSharingContentType?.(PersonalInformationSharingContentType.Email)
      }
    }

    if ('errors' in response) {
      setSendMessageUi({ uiState: UiState.Failure, error: response.errors?.[0]?.value })

      return
    }

    if (conversation.isSuspicious) await deleteConversationSuspicion(conversation.id)

    setSendMessageUi({ uiState: UiState.Success })
    const transformedMessages = transformMessagesEntities(response.conversation.messages)
    setConversationMessages(transformedMessages)

    if (webPostReplyOptimizationAbTest?.variant !== 'on') {
      getConversationRequest({
        conversationId: conversation.id,
        fromPostReply: true,
      })

      if ('conversation' in response)
        setConversation(transformConversationResponse(response.conversation))
    }

    track(conversationReplyEvent({ userId, itemId: conversation.itemId ?? null }))
  }

  const postReplyRequestNew = async ({
    value,
    photoTempUuids,
    isPersonalDataSharingCheckSkipped = false,
    onSetPersonalInformationSharingContentType,
  }: Props) => {
    setSendMessageUi({ uiState: UiState.Pending })

    if (!conversation?.id || !userId) return

    const response = await postReplyNew({
      conversationId: conversation.id.toString(),
      content: value,
      photoTempUuids,
      isPersonalDataSharingCheckSkipped,
    })

    if (response?.code === ResponseCode.EmailSharingDetected && 'errors' in response) {
      if (
        response.payload &&
        typeof response.payload === 'object' &&
        'content_type' in response.payload
      ) {
        onSetPersonalInformationSharingContentType?.(
          response.payload.content_type as PersonalInformationSharingContentType,
        )
      } else {
        onSetPersonalInformationSharingContentType?.(PersonalInformationSharingContentType.Email)
      }
    }

    if ('errors' in response) {
      setSendMessageUi({ uiState: UiState.Failure, error: response.errors?.[0]?.value })

      return
    }

    if (conversation.isSuspicious) await deleteConversationSuspicion(conversation.id)

    setSendMessageUi({ uiState: UiState.Success })

    const newMessage = transformPostReplyNewResp(response)

    setConversationMessages([...conversationMessages, newMessage])

    track(conversationReplyEvent({ userId, itemId: conversation.itemId ?? null }))
  }

  const postReplyRequest = isWebMessagingSvcFSEnabled
    ? postReplyRequestNew
    : postReplyRequestCurrent

  return { postReplyRequest }
}

export default usePostReply
